/* ==========================================================================
   Covet & Mane — GSAP motion layer
   Three behaviours, all opt-in via data attributes so sections stay declarative:

     [data-cm-reveal]        element fades/slides in when scrolled into view
     [data-cm-reveal-group]  staggers the [data-cm-reveal] children inside it
     [data-cm-count]         animates every number in the element up from zero

   Plus <cm-sticky-cta>, which reveals itself once its target (the hero) has
   left the viewport.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT = document.documentElement;
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;

  /* GSAP failed to load (blocked, offline, asset renamed). Drop the class the
     stylesheet uses to pre-hide reveal targets so nothing stays invisible. */
  if (!gsap || !ScrollTrigger) {
    ROOT.classList.remove('cm-anim');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  var REVEAL_FROM = {
    up: { y: 32, opacity: 0 },
    down: { y: -32, opacity: 0 },
    left: { x: -40, opacity: 0 },
    right: { x: 40, opacity: 0 },
    fade: { opacity: 0 },
    scale: { opacity: 0, scale: 0.96 }
  };

  function num(value, fallback) {
    var parsed = parseFloat(value);
    return isNaN(parsed) ? fallback : parsed;
  }

  /* ------------------------------------------------------------------
     Scroll reveals
     ------------------------------------------------------------------ */

  function revealTargets(scope) {
    return Array.prototype.slice.call(scope.querySelectorAll('[data-cm-reveal]'));
  }

  function initReveals(scope) {
    var elements = revealTargets(scope);
    if (!elements.length) return;

    if (REDUCED) {
      gsap.set(elements, { opacity: 1, x: 0, y: 0, scale: 1, clearProps: 'willChange' });
      return;
    }

    /* Bucket every reveal under its nearest group so siblings share one
       trigger and stagger together instead of firing independently. */
    var groups = [];
    var byGroup = new Map();

    elements.forEach(function (el) {
      var group = el.closest('[data-cm-reveal-group]');
      var container = group && scope.contains(group) ? group : null;

      if (!container) {
        groups.push({ trigger: el, items: [el], stagger: 0 });
        return;
      }
      if (!byGroup.has(container)) {
        var entry = {
          trigger: container,
          items: [],
          stagger: num(container.getAttribute('data-cm-reveal-stagger'), 0.1)
        };
        byGroup.set(container, entry);
        groups.push(entry);
      }
      byGroup.get(container).items.push(el);
    });

    groups.forEach(function (group) {
      group.items.forEach(function (el) {
        var direction = el.getAttribute('data-cm-reveal') || 'up';
        gsap.set(el, REVEAL_FROM[direction] || REVEAL_FROM.up);
      });

      gsap.to(group.items, {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        duration: num(group.trigger.getAttribute('data-cm-reveal-duration'), 0.9),
        ease: 'power3.out',
        stagger: group.stagger,
        overwrite: 'auto',
        clearProps: 'willChange',
        scrollTrigger: {
          trigger: group.trigger,
          start: group.trigger.getAttribute('data-cm-reveal-start') || 'top 85%',
          once: true
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Count-up stats
     Splits "$1,600–$2,000" into text and number parts, wraps each number in
     a span, then tweens all of them together — so currency symbols, ranges,
     "+" suffixes and thousands separators all survive.
     ------------------------------------------------------------------ */

  var NUMBER_PATTERN = /\d[\d,]*(?:\.\d+)?/g;

  function buildCounter(el) {
    var source = (el.textContent || '').trim();
    if (!source) return null;

    var parts = [];
    var lastIndex = 0;
    var match;

    NUMBER_PATTERN.lastIndex = 0;
    while ((match = NUMBER_PATTERN.exec(source)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: source.slice(lastIndex, match.index) });
      }
      var raw = match[0];
      var decimals = raw.indexOf('.') > -1 ? raw.split('.')[1].length : 0;
      parts.push({
        value: parseFloat(raw.replace(/,/g, '')),
        grouped: raw.indexOf(',') > -1,
        decimals: decimals
      });
      lastIndex = match.index + raw.length;
    }

    if (!parts.some(function (part) { return part.value !== undefined; })) return null;
    if (lastIndex < source.length) parts.push({ text: source.slice(lastIndex) });

    var fragment = document.createDocumentFragment();
    var numbers = [];

    parts.forEach(function (part) {
      if (part.value === undefined) {
        fragment.appendChild(document.createTextNode(part.text));
        return;
      }
      var span = document.createElement('span');
      span.className = 'cm-count__value';
      span.textContent = format(0, part);
      fragment.appendChild(span);
      numbers.push({ node: span, target: part, current: 0 });
    });

    el.textContent = '';
    el.appendChild(fragment);

    return numbers;
  }

  function format(value, part) {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: part.decimals,
      maximumFractionDigits: part.decimals,
      useGrouping: part.grouped
    });
  }

  function initCounters(scope) {
    var elements = Array.prototype.slice.call(scope.querySelectorAll('[data-cm-count]'));

    elements.forEach(function (el) {
      if (el.dataset.cmCountReady === 'true') return;

      var numbers = buildCounter(el);
      if (!numbers) return;
      el.dataset.cmCountReady = 'true';

      if (REDUCED) {
        numbers.forEach(function (entry) {
          entry.node.textContent = format(entry.target.value, entry.target);
        });
        return;
      }

      var proxies = numbers.map(function (entry) { return { n: 0 }; });

      ScrollTrigger.create({
        trigger: el,
        start: 'top 88%',
        once: true,
        onEnter: function () {
          numbers.forEach(function (entry, index) {
            gsap.to(proxies[index], {
              n: entry.target.value,
              duration: num(el.getAttribute('data-cm-count-duration'), 0.75),
              ease: 'power2.out',
              onUpdate: function () {
                entry.node.textContent = format(proxies[index].n, entry.target);
              },
              onComplete: function () {
                entry.node.textContent = format(entry.target.value, entry.target);
              }
            });
          });
        }
      });
    });
  }

  /* ------------------------------------------------------------------
     Sticky CTA — visible only while the hero is out of view
     ------------------------------------------------------------------ */

  if (!window.customElements.get('cm-sticky-cta')) {
    window.customElements.define(
      'cm-sticky-cta',
      class CmStickyCta extends HTMLElement {
        connectedCallback() {
          this.panel = this.querySelector('[data-cm-sticky-panel]');
          if (!this.panel) return;

          this.visible = false;
          gsap.set(this.panel, { autoAlpha: 0, xPercent: 12 });

          var selector = this.getAttribute('data-cm-watch') || '[data-cm-hero]';
          this.watch = document.querySelector(selector);

          this.trigger = ScrollTrigger.create({
            trigger: this.watch || document.body,
            start: this.watch ? 'bottom top' : 'top top-=60%',
            end: 'max',
            onToggle: (self) => this.setVisible(self.isActive),
            onRefresh: (self) => this.setVisible(self.isActive)
          });
        }

        disconnectedCallback() {
          if (this.trigger) this.trigger.kill();
        }

        setVisible(next) {
          if (next === this.visible) return;
          this.visible = next;

          this.panel.setAttribute('aria-hidden', next ? 'false' : 'true');
          this.classList.toggle('is-visible', next);

          if (REDUCED) {
            gsap.set(this.panel, { autoAlpha: next ? 1 : 0, xPercent: 0 });
            return;
          }

          gsap.to(this.panel, {
            autoAlpha: next ? 1 : 0,
            xPercent: next ? 0 : 12,
            duration: 0.5,
            ease: next ? 'power3.out' : 'power2.in'
          });
        }
      }
    );
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function init(scope) {
    initReveals(scope || document);
    initCounters(scope || document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* Theme editor swaps section markup in place. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:unload', function () {
    ScrollTrigger.refresh();
  });

  window.addEventListener('load', function () { ScrollTrigger.refresh(); });
})();
